import { QueryKeys, useEntities } from "@/api/core";
import { useFeatures } from "@/hooks";
import { type Ad, adSchema } from "@/schemas";
import { isExpired } from "@/utils/ads";

interface Params {
  zone?: number;
}

const ADS_API_VERSIONS = ["v4", "v5", "v6"] as const;
type AdsApiVersion = (typeof ADS_API_VERSIONS)[number];

function getAdsEndpoint(apiVersion: AdsApiVersion) {
  return `/api/${apiVersion}/truth/ads`;
}

function useAds(
  params: Params = {},
  options: {
    isEnabled: boolean;
    instanceId: string;
    apiVersion?: AdsApiVersion;
  },
) {
  const { isEnabled, instanceId, apiVersion = "v6" } = options;
  const features = useFeatures();

  return useEntities<Ad>({
    queryKey: QueryKeys.ads.list({
      ...params,
      instanceId,
      apiVersion,
    }),
    endpoint: getAdsEndpoint(apiVersion),
    enabled: isEnabled,
    params: {
      ...params,
      device: "desktop",
      ...(features.htmlAds && { enable_html: true }),
    },
    schema: adSchema,
    formatResult(result, response) {
      return result.filter((ad) => {
        const metrics = ad.status?.metrics || ad.ad?.metrics;

        return metrics && !isExpired(metrics);
      });
    },
  });
}

export { useAds, getAdsEndpoint, ADS_API_VERSIONS, type AdsApiVersion };
